import { useCallback, useEffect, useState } from 'react';
import { Fingerprint, ScanFace, Trash2, X } from 'lucide-react';
import { api } from '../../lib/api';
import { ago } from '../../lib/format';
import { useT, useLang } from '../../lib/i18n';
import {
  addPasskey,
  biometricName,
  passkeyError,
  passkeysAvailable,
  phoneKeyAsks,
  phoneSignInWith,
  phoneUnlockAvailable,
  phoneUnlockSignsIn,
  savedPhoneKey,
  setPhoneKeyAsks,
  turnOffPhoneUnlock,
  turnOnPhoneUnlock,
} from '../../lib/passkey';
import { Segmented } from '../../ui';
import { toast } from '../../lib/session';

type Key = { id: string; name: string; created: string; lastUsed?: string; kind?: 'passkey' | 'phone' };

const Icon = ({ size = 16 }: { size?: number }) => (biometricName() === 'Face ID' || phoneUnlockAvailable() ? <ScanFace size={size} /> : <Fingerprint size={size} />);

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Sets up sign-in on this device. In the Android app that's face unlock (a phone key the app
 * releases after any face or fingerprint unlock, since passkeys there take only the fingerprint
 * on most phones); elsewhere a passkey. Returns false if cancelled.
 */
async function setUpHere(): Promise<boolean> {
  if (phoneUnlockAvailable()) return turnOnPhoneUnlock();
  await addPasskey();
  return true;
}

/** Settings → Security: this account's Face ID / fingerprint devices. */
export function PasskeysCard() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const [keys, setKeys] = useState<Key[] | null>(null);
  const [can, setCan] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.passkeys().then(setKeys, () => setKeys([])), []);
  useEffect(() => {
    load();
    if (phoneUnlockAvailable()) setCan(true);
    else passkeysAvailable().then(setCan);
  }, [load]);
  const here = savedPhoneKey();
  const [asks, setAsks] = useState(phoneKeyAsks);
  const method = biometricName();
  const add = async () => {
    setBusy(true);
    try {
      if (!(await setUpHere())) return;
      markAsked();
      toast(capital(t('passkey.added', { method })), 'accent');
      load();
    } catch (e) {
      const msg = passkeyError(e);
      if (msg) toast(msg, 'red');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card" data-anim>
      <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon size={17} /> {t('passkey.title')}
      </h2>
      <p className="app-lead">{phoneUnlockSignsIn() ? t('passkey.leadPhone') : t('passkey.lead', { method })}</p>
      {keys && keys.length > 0 ? (
        <div className="passkey-list">
          {keys.map((k) => (
            <div className="passkey-row" key={k.id}>
              {k.kind === 'phone' ? <ScanFace size={16} /> : <Fingerprint size={16} />}
              <div>
                <b className="truncate">
                  {k.name}
                  {k.id === here && <em className="passkey-here"> · {t('passkey.thisPhone')}</em>}
                </b>
                <span>{k.lastUsed ? t('passkey.lastUsed', { when: ago(k.lastUsed, lang) }) : t('passkey.neverUsed')}</span>
              </div>
              <button
                className="icon-btn"
                aria-label={t('passkey.remove')}
                title={t('passkey.remove')}
                onClick={() =>
                  (k.id === here ? turnOffPhoneUnlock() : api.deletePasskey(k.id))
                    .then(load)
                    .catch((e) => toast((e as Error).message, 'red'))
                }
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        keys && <p className="muted" style={{ fontSize: 13 }}>{t('passkey.none')}</p>
      )}
      {here && asks && keys?.some((k) => k.id === here) && (
        <div style={{ marginBottom: 12 }}>
          <div className="row" style={{ fontSize: 13, gap: 10, flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>{t('passkey.signInWith')}</span>
            <Segmented
              value={phoneSignInWith()}
              label={t('passkey.signInWith')}
              options={[
                { value: 'face', label: <><ScanFace size={14} /> {t('passkey.faceTab')}</> },
                { value: 'fingerprint', label: <><Fingerprint size={14} /> {t('passkey.fingerprintTab')}</> },
              ]}
              onChange={(v) => {
                setPhoneKeyAsks(v === 'fingerprint');
                setAsks(phoneKeyAsks());
              }}
            />
          </div>
          <p className="muted" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
            {asks === 'on' ? t('passkey.fingerprintHint') : t('passkey.faceHint')}
          </p>
        </div>
      )}
      {here && keys?.some((k) => k.id === here) ? null : can === false ? (
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
          {t('passkey.unavailable')}
        </p>
      ) : (
        <button className="btn btn-black" disabled={busy || can === null} onClick={add}>
          <Icon /> {t('passkey.add', { method })}
        </button>
      )}
    </section>
  );
}

// The Android app asks again about face unlock, even where a fingerprint passkey was offered before.
const ASKED = phoneUnlockAvailable() ? 'vb.faceUnlockAsked' : 'vb.passkeyAsked';
const asked = () => {
  try {
    return localStorage.getItem(ASKED) === '1';
  } catch {
    return true;
  }
};
const markAsked = () => {
  try {
    localStorage.setItem(ASKED, '1');
  } catch {
    /* private mode */
  }
};

/** Once per device: offer Face ID / fingerprint sign-in if this device can and has none yet. */
export function PasskeyNudge() {
  const t = useT();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (asked()) return;
    if (phoneUnlockAvailable()) {
      setShow(!savedPhoneKey());
      return;
    }
    passkeysAvailable().then((ok) => {
      if (ok) api.passkeys().then((k) => setShow(k.length === 0), () => undefined);
    });
  }, []);
  if (!show) return null;
  const method = biometricName();
  return (
    <div className="passkey-nudge" data-anim>
      <Icon size={18} />
      <span>{t('passkey.nudge', { method })}</span>
      <button
        className="btn btn-accent btn-sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            if (!(await setUpHere())) return;
            toast(capital(t('passkey.added', { method })), 'accent');
            markAsked();
            setShow(false);
          } catch (e) {
            const msg = passkeyError(e);
            if (msg) toast(msg, 'red');
          } finally {
            setBusy(false);
          }
        }}
      >
        {t('push.turnOn')}
      </button>
      <button
        className="icon-btn"
        style={{ color: 'rgba(255,255,255,.7)' }}
        aria-label={t('passkey.nudgeLater')}
        onClick={() => {
          markAsked();
          setShow(false);
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
